// Schema.org JSON-LD builders. Only real, configured URLs are emitted — never invented links.
export const ARTIST_NAME = "DJ Código";
export const ARTIST_ALT = "DJ Codigo";
export const GENRES = ["Latin Urban", "Reggaetón", "Latin Hip-Hop", "Caribbean Club Music"];

export function siteUrl(settings) {
  return (settings?.seo?.siteUrl || window.location.origin).replace(/\/$/, "");
}

export function sameAs(settings) {
  const s = settings?.socials || {};
  const st = settings?.streaming || {};
  return [st.spotify, st.apple_music, st.youtube_music, s.youtube, s.instagram, s.tiktok, s.facebook].filter(Boolean);
}

export function artistSchema(settings) {
  const url = siteUrl(settings);
  return {
    "@context": "https://schema.org",
    "@type": "MusicGroup",
    "@id": `${url}/#artist`,
    name: ARTIST_NAME,
    alternateName: ARTIST_ALT,
    url,
    genre: GENRES,
    description: settings?.bioShort || settings?.bio,
    image: settings?.artistImage,
    sameAs: sameAs(settings),
    foundingLocation: { "@type": "Country", name: "Dominican Republic" },
  };
}

function artistRef(settings) {
  return { "@type": "MusicGroup", "@id": `${siteUrl(settings)}/#artist`, name: ARTIST_NAME, alternateName: ARTIST_ALT, url: siteUrl(settings) };
}

export function releaseSchema(release, settings) {
  const url = `${siteUrl(settings)}/music/${release.slug}`;
  const isAlbum = release.releaseType === "album" || release.releaseType === "ep";
  const links = [release.spotifyURL, release.appleMusicURL, release.youtubeMusicURL, release.youtubeURL, release.amazonMusicURL, release.deezerURL, release.tidalURL].filter(Boolean);
  const base = {
    "@context": "https://schema.org",
    "@type": isAlbum ? "MusicAlbum" : "MusicRecording",
    name: release.title,
    url,
    image: release.artwork,
    byArtist: artistRef(settings),
    datePublished: release.releaseDate,
    genre: release.genre || GENRES,
    description: release.seoDescription || release.description,
    sameAs: links,
    ...(links.length ? { potentialAction: { "@type": "ListenAction", target: links } } : {}),
  };
  if (isAlbum) {
    base.albumReleaseType = release.releaseType === "ep" ? "EPRelease" : "AlbumRelease";
    base.numTracks = release.tracks?.length || undefined;
    base.track = (release.tracks || []).map((t, i) => ({
      "@type": "MusicRecording", name: t.title, position: t.trackNumber || i + 1,
      duration: t.durationMs ? `PT${Math.floor(t.durationMs / 60000)}M${Math.floor((t.durationMs % 60000) / 1000)}S` : undefined,
      byArtist: artistRef(settings),
    }));
  } else {
    if (release.isrc) base.isrcCode = release.isrc;
    const d = release.tracks?.[0]?.durationMs;
    if (d) base.duration = `PT${Math.floor(d / 60000)}M${Math.floor((d % 60000) / 1000)}S`;
  }
  return base;
}

export function videoSchema(video, settings) {
  return {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: video.title,
    description: video.description || `${video.title} by DJ Código`,
    thumbnailUrl: video.thumbnail || `https://i.ytimg.com/vi/${video.youtubeId}/maxresdefault.jpg`,
    uploadDate: video.publishedAt || video.createdAt,
    embedUrl: `https://www.youtube.com/embed/${video.youtubeId}`,
    contentUrl: `https://www.youtube.com/watch?v=${video.youtubeId}`,
    url: `${siteUrl(settings)}/videos/${video.slug}`,
    author: artistRef(settings),
  };
}

export function breadcrumbSchema(items, settings) {
  const url = siteUrl(settings);
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({ "@type": "ListItem", position: i + 1, name: it.name, item: `${url}${it.path}` })),
  };
}

export function websiteSchema(settings) {
  const url = siteUrl(settings);
  return { "@context": "https://schema.org", "@type": "WebSite", name: ARTIST_NAME, alternateName: ARTIST_ALT, url, publisher: artistRef(settings) };
}
